import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { toastMessage } from "../../utils/toastMessage";

const MetricManager = ({ systemMetrics, customMetrics = [], onMetricsConfigUpdate }) => {
  const [metricsConfig, setMetricsConfig] = useState([]);
  const [customMetricsConfig, setCustomMetricsConfig] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Define all system metrics with categories
  const allSystemMetrics = [
    // User Lifecycle
    { key: "totalSignup", label: "Total Signup", category: "User Lifecycle", defaultVisible: true },
    { key: "totalSignout", label: "Total Signout", category: "User Lifecycle", defaultVisible: true },
    { key: "accountDeleted", label: "Account Deleted", category: "User Lifecycle", defaultVisible: true },
    { key: "totalActiveUsers", label: "Total Active Users", category: "User Lifecycle", defaultVisible: true },
    { key: "avgActiveTime", label: "Avg. Active Time", category: "User Lifecycle", defaultVisible: true },
    
    // Consultation
    { key: "firstConsultation", label: "First Consultation", category: "Consultation", defaultVisible: true },
    { key: "repeatConsultation", label: "Repeat Consultation", category: "Consultation", defaultVisible: true },
    { key: "totalPaidChatConsultation", label: "Total Paid Chat Consultation", category: "Consultation", defaultVisible: true },
    { key: "totalPaidCallConsultation", label: "Total Paid Call Consultation", category: "Consultation", defaultVisible: true },
    
    // Call Retention
    { key: "firstCallPaidConsultation", label: "1st Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    { key: "secondCallPaidConsultation", label: "2nd Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    { key: "thirdCallPaidConsultation", label: "3rd Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    { key: "fourthCallPaidConsultation", label: "4th Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    { key: "fifthCallPaidConsultation", label: "5th Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    { key: "sixthCallPaidConsultation", label: "6th Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    { key: "seventhCallPaidConsultation", label: "7th Call Paid Consultation", category: "Call Retention", defaultVisible: true },
    
    // Chat Retention
    { key: "firstChatPaidConsultation", label: "1st Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    { key: "secondChatPaidConsultation", label: "2nd Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    { key: "thirdChatPaidConsultation", label: "3rd Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    { key: "fourthChatPaidConsultation", label: "4th Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    { key: "fifthChatPaidConsultation", label: "5th Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    { key: "sixthChatPaidConsultation", label: "6th Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    { key: "seventhChatPaidConsultation", label: "7th Chat Paid Consultation", category: "Chat Retention", defaultVisible: true },
    
    // Free Chat Attempts
    { key: "freeCanceledChatConsultation", label: "Free Canceled Chat Consultation", category: "Free Chat Attempts", defaultVisible: true },
    
    // Free Call Attempts
    { key: "freeCancelledCallConsultation", label: "Free Cancelled Call Consultation", category: "Free Call Attempts", defaultVisible: true },
    
    // Paid Call Issues
    { key: "paidMissedCallConsultation", label: "Paid Missed Call Consultation", category: "Paid Call Issues", defaultVisible: true },
    { key: "paidMissedChatConsultation", label: "Paid Missed Chat Consultation", category: "Paid Call Issues", defaultVisible: true },
    
    // Wallet & Revenue
    { key: "noOfFirstRecharge", label: "No. of First Recharge", category: "Wallet & Revenue", defaultVisible: true },
    { key: "totalNoOfRecharge", label: "Total No. of Recharge", category: "Wallet & Revenue", defaultVisible: true },
    { key: "firstRechargeAmount", label: "First Recharge Amount", category: "Wallet & Revenue", defaultVisible: true },
    { key: "totalRechargeAmount", label: "Total Recharge Amount", category: "Wallet & Revenue", defaultVisible: true },
    { key: "spendOnCall", label: "Spend on Call", category: "Wallet & Revenue", defaultVisible: true },
    { key: "spendOnChat", label: "Spend on Chat", category: "Wallet & Revenue", defaultVisible: true },
    { key: "totalCoinsInWallet", label: "Total Coins in Wallet", category: "Wallet & Revenue", defaultVisible: true },
    
    // Time Metrics
    { key: "timeSpentOnChat", label: "time spent on chat", category: "Time Metrics", defaultVisible: true },
    { key: "timeSpentOnCall", label: "time spent on Call", category: "Time Metrics", defaultVisible: true },
  ];

  useEffect(() => {
    loadMetricsConfig();
    loadCustomMetricsConfig();
  }, [customMetrics]);

  const loadMetricsConfig = () => {
    try {
      const saved = localStorage.getItem("systemMetricsConfig");
      if (saved) {
        const savedConfig = JSON.parse(saved);
        // Merge with defaults to handle new metrics
        const mergedConfig = allSystemMetrics.map((metric) => {
          const saved = savedConfig.find((m) => m.key === metric.key);
          return saved || { ...metric, visible: metric.defaultVisible };
        });
        setMetricsConfig(mergedConfig);
      } else {
        // Initialize with defaults
        setMetricsConfig(
          allSystemMetrics.map((m) => ({ ...m, visible: m.defaultVisible }))
        );
      }
    } catch (error) {
      console.error("Error loading metrics config:", error);
      setMetricsConfig(
        allSystemMetrics.map((m) => ({ ...m, visible: m.defaultVisible }))
      );
    }
  };

  const loadCustomMetricsConfig = () => {
    try {
      // Load custom metrics from localStorage (stored by CustomMetricsBuilder)
      const savedCustomMetrics = localStorage.getItem("customMetrics");
      const customMetricsList = savedCustomMetrics ? JSON.parse(savedCustomMetrics) : [];
      
      // Load visibility config for custom metrics
      const savedCustomConfig = localStorage.getItem("customMetricsConfig");
      if (savedCustomConfig) {
        const savedConfig = JSON.parse(savedCustomConfig);
        // Merge with current custom metrics
        const mergedConfig = customMetricsList.map((metric) => {
          const saved = savedConfig.find((m) => m.id === metric.id);
          return saved || { ...metric, visible: true, isCustom: true };
        });
        setCustomMetricsConfig(mergedConfig);
      } else {
        // Initialize with all visible
        setCustomMetricsConfig(
          customMetricsList.map((m) => ({ ...m, visible: true, isCustom: true }))
        );
      }
    } catch (error) {
      console.error("Error loading custom metrics config:", error);
      setCustomMetricsConfig([]);
    }
  };

  const handleToggleMetric = (key) => {
    setMetricsConfig((prev) =>
      prev.map((metric) =>
        metric.key === key ? { ...metric, visible: !metric.visible } : metric
      )
    );
    setHasChanges(true);
  };

  const handleToggleCustomMetric = (id) => {
    setCustomMetricsConfig((prev) =>
      prev.map((metric) =>
        metric.id === id ? { ...metric, visible: !metric.visible } : metric
      )
    );
    setHasChanges(true);
  };

  const handleSelectAll = (category) => {
    setMetricsConfig((prev) =>
      prev.map((metric) =>
        metric.category === category
          ? { ...metric, visible: true }
          : metric
      )
    );
    setHasChanges(true);
  };

  const handleDeselectAll = (category) => {
    setMetricsConfig((prev) =>
      prev.map((metric) =>
        metric.category === category
          ? { ...metric, visible: false }
          : metric
      )
    );
    setHasChanges(true);
  };

  const handleSave = () => {
    try {
      localStorage.setItem("systemMetricsConfig", JSON.stringify(metricsConfig));
      localStorage.setItem("customMetricsConfig", JSON.stringify(customMetricsConfig));
      if (onMetricsConfigUpdate) {
        onMetricsConfigUpdate({ system: metricsConfig, custom: customMetricsConfig });
      }
      setHasChanges(false);
      toastMessage("Metrics configuration saved successfully", "success");
    } catch (error) {
      console.error("Error saving metrics config:", error);
      toastMessage("Error saving configuration", "error");
    }
  };

  const handleReset = () => {
    setMetricsConfig(
      allSystemMetrics.map((m) => ({ ...m, visible: m.defaultVisible }))
    );
    setHasChanges(true);
  };

  const handleResetToDefaults = () => {
    setMetricsConfig(
      allSystemMetrics.map((m) => ({ ...m, visible: m.defaultVisible }))
    );
    // Reset custom metrics to all visible
    const savedCustomMetrics = localStorage.getItem("customMetrics");
    const customMetricsList = savedCustomMetrics ? JSON.parse(savedCustomMetrics) : [];
    setCustomMetricsConfig(
      customMetricsList.map((m) => ({ ...m, visible: true, isCustom: true }))
    );
    localStorage.removeItem("systemMetricsConfig");
    localStorage.removeItem("customMetricsConfig");
    if (onMetricsConfigUpdate) {
      onMetricsConfigUpdate({
        system: allSystemMetrics.map((m) => ({ ...m, visible: m.defaultVisible })),
        custom: customMetricsList.map((m) => ({ ...m, visible: true, isCustom: true }))
      });
    }
    setHasChanges(false);
    toastMessage("Reset to default configuration", "success");
  };

  // Group metrics by category
  const metricsByCategory = metricsConfig.reduce((acc, metric) => {
    if (!acc[metric.category]) {
      acc[metric.category] = [];
    }
    acc[metric.category].push(metric);
    return acc;
  }, {});

  const getCategoryStats = (category) => {
    const categoryMetrics = metricsConfig.filter((m) => m.category === category);
    const visibleCount = categoryMetrics.filter((m) => m.visible).length;
    return { total: categoryMetrics.length, visible: visibleCount };
  };

  return (
    <Box>
      <Card sx={{ mb: 3, borderRadius: 2, bgcolor: "#f5f5f5" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff8989", mb: 1 }}>
                Manage System Metrics
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Show or hide system-generated metrics on the dashboard. Changes are saved automatically.
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleResetToDefaults}
                sx={{
                  borderColor: "#ff8989",
                  color: "#ff8989",
                  "&:hover": { borderColor: "#ff7070", backgroundColor: "#ff898915" },
                }}
              >
                Reset to Defaults
              </Button>
              {hasChanges && (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{
                    backgroundColor: "#ff8989",
                    "&:hover": { backgroundColor: "#ff7070" },
                  }}
                >
                  Save Changes
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}

      {Object.keys(metricsByCategory).map((category) => {
        const stats = getCategoryStats(category);
        return (
          <Card key={category} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                    {category} Metrics
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    {stats.visible} of {stats.total} metrics visible
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    onClick={() => handleSelectAll(category)}
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Show All
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleDeselectAll(category)}
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Hide All
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {metricsByCategory[category].map((metric) => (
                  <Grid item xs={12} sm={6} md={4} key={metric.key}>
                    <Card
                      sx={{
                        border: `1px solid ${metric.visible ? "#4caf50" : "#e0e0e0"}`,
                        borderRadius: 2,
                        bgcolor: metric.visible ? "#f1f8f4" : "#fafafa",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box flex={1}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: metric.visible ? "#333" : "#999",
                                mb: 0.5,
                              }}
                            >
                              {metric.label}
                            </Typography>
                            <Chip
                              label={metric.visible ? "Visible" : "Hidden"}
                              size="small"
                              icon={
                                metric.visible ? (
                                  <VisibilityIcon fontSize="small" />
                                ) : (
                                  <VisibilityOffIcon fontSize="small" />
                                )
                              }
                              sx={{
                                backgroundColor: metric.visible
                                  ? "#4caf5015"
                                  : "#f4433615",
                                color: metric.visible ? "#4caf50" : "#f44336",
                                fontSize: "0.7rem",
                                height: "20px",
                              }}
                            />
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={metric.visible}
                                onChange={() => handleToggleMetric(metric.key)}
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#4caf50",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#4caf50",
                                  },
                                }}
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      {/* Custom Metrics Section */}
      {customMetricsConfig.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, border: "2px solid #ff8989" }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff8989" }}>
                  Custom Metrics
                </Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {customMetricsConfig.filter((m) => m.visible).length} of {customMetricsConfig.length} custom metrics visible
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  onClick={() => {
                    setCustomMetricsConfig((prev) =>
                      prev.map((m) => ({ ...m, visible: true }))
                    );
                    setHasChanges(true);
                  }}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Show All
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setCustomMetricsConfig((prev) =>
                      prev.map((m) => ({ ...m, visible: false }))
                    );
                    setHasChanges(true);
                  }}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Hide All
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {customMetricsConfig.map((metric) => (
                <Grid item xs={12} sm={6} md={4} key={metric.id}>
                  <Card
                    sx={{
                      border: `1px solid ${metric.visible ? "#ff8989" : "#e0e0e0"}`,
                      borderRadius: 2,
                      bgcolor: metric.visible ? "#fff5f5" : "#fafafa",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box flex={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: metric.visible ? "#333" : "#999",
                              mb: 0.5,
                            }}
                          >
                            {metric.name}
                          </Typography>
                          {metric.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#666",
                                display: "block",
                                mb: 0.5,
                                fontStyle: "italic",
                              }}
                            >
                              {metric.description}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#999",
                              fontFamily: "monospace",
                              fontSize: "0.7rem",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            {metric.formula}
                          </Typography>
                          <Chip
                            label={metric.visible ? "Visible" : "Hidden"}
                            size="small"
                            icon={
                              metric.visible ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )
                            }
                            sx={{
                              backgroundColor: metric.visible
                                ? "#ff898915"
                                : "#f4433615",
                              color: metric.visible ? "#ff8989" : "#f44336",
                              fontSize: "0.7rem",
                              height: "20px",
                            }}
                          />
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={metric.visible}
                              onChange={() => handleToggleCustomMetric(metric.id)}
                              size="small"
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#ff8989",
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: "#ff8989",
                                },
                              }}
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {customMetricsConfig.length === 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, p: 3, textAlign: "center", bgcolor: "#f5f5f5" }}>
          <Typography variant="body2" sx={{ color: "#666" }}>
            No custom metrics created yet. Go to "Custom Metrics" tab to create custom metrics.
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default MetricManager;

